import type { AiOperationsRun, RiskLevel } from "@adpilot/shared";
import { IconFilter, IconRobot, IconSparkles, IconUser } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalStatusBadge } from "@/components/approval/approval-status-badge";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableDisclosure, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { ErrorState } from "@/components/shared/error-state";
import { RiskLegendTip } from "@/components/shared/RiskLegendTip";
import { Badge } from "@/components/ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  runDisplayStatus,
  runOutcome,
  runResultDetail,
  type TranslateFn,
} from "@/lib/ai-run-presentation";
import { useFormatters } from "@/lib/format";
import { formatBeijingDate, formatBeijingTime } from "@/lib/time";
import { cn } from "@/lib/utils";

interface AiRunHistoryTableProps {
  runs: AiOperationsRun[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpenRun: (runId: string) => void;
  initialManagedGroupId?: string;
}

type RunStatusFilter =
  | "all"
  | "needs-decision"
  | "approved"
  | "executed"
  | "rolled-back"
  | "rejected"
  | "expired"
  | "recommended"
  | "partial"
  | "analyzing"
  | "failed"
  | "completed";

interface BatchRunRecord {
  kind: "batch";
  id: string;
  triggerType: AiOperationsRun["triggerType"];
  runs: AiOperationsRun[];
}

interface SingleRunRecord {
  kind: "single";
  id: string;
  run: AiOperationsRun;
}

type RunRecord = BatchRunRecord | SingleRunRecord;

const STATUS_FILTER_VALUES = [
  "all",
  "needs-decision",
  "approved",
  "executed",
  "rolled-back",
  "rejected",
  "expired",
  "recommended",
  "partial",
  "analyzing",
  "failed",
  "completed",
] as const;
export function AiRunHistoryTable({
  runs,
  isLoading,
  isError,
  onRetry,
  onOpenRun,
  initialManagedGroupId,
}: AiRunHistoryTableProps): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const statusFilterItems = STATUS_FILTER_VALUES.map((value) => ({
    value,
    label: t(`history.statusFilter.${value}`),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<RunStatusFilter>("all");
  const [groupFilter, setGroupFilter] = useState(initialManagedGroupId ?? "all");
  const [collapsedBatchIds, setCollapsedBatchIds] = useState<Set<string>>(() => new Set());
  const groupFilterItems = useMemo(
    () => [
      { value: "all", label: t("history.groupFilter.all") },
      ...[
        ...new Map(
          runs.flatMap((run) =>
            run.managedGroupId
              ? [
                  [
                    run.managedGroupId,
                    run.managedGroupName ?? run.managedGroupId.slice(0, 8),
                  ] as const,
                ]
              : [],
          ),
        ).entries(),
      ]
        .sort((left, right) => left[1].localeCompare(right[1]))
        .map(([value, label]) => ({ value, label })),
      { value: "ungrouped", label: t("history.groupFilter.ungrouped") },
    ],
    [runs, t],
  );
  const groupRuns = useMemo(() => filterRunsByManagedGroup(runs, groupFilter), [groupFilter, runs]);
  const records = useMemo(() => buildRunRecords(groupRuns), [groupRuns]);
  const filteredRecords = useMemo(
    () => filterRunRecords(records, statusFilter),
    [records, statusFilter],
  );
  const filteredRunCount = filteredRecords.reduce(
    (total, record) => total + recordRuns(record).length,
    0,
  );
  const pageCount = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visibleRecords = filteredRecords.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  useEffect(() => {
    setGroupFilter(initialManagedGroupId ?? "all");
    setPage(1);
  }, [initialManagedGroupId]);

  function selectStatus(value: string): void {
    if (isRunStatusFilter(value)) {
      setStatusFilter(value);
      setPage(1);
    }
  }

  function selectGroup(value: string): void {
    setGroupFilter(value);
    setPage(1);
  }

  function toggleBatch(batchId: string): void {
    setCollapsedBatchIds((current) => {
      const next = new Set(current);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  }

  return (
    <DataTableFrame
      aria-label={t("history.ariaLabel")}
      className="flex min-h-[400px] flex-1 flex-col"
      viewportClassName="flex min-h-0 flex-1 flex-col"
      toolbar={
        <>
          <div className="flex items-baseline gap-[7px]">
            <h2 className="text-sm font-semibold leading-[18px] text-ink">{t("history.title")}</h2>
            <span className="text-[10px] leading-3.5 text-muted-foreground">
              {t("history.summary", { records: filteredRecords.length, runs: filteredRunCount })}
            </span>
          </div>
          <FilterSelect
            ariaLabel={t("history.filterAria")}
            icon={<IconFilter aria-hidden />}
            items={statusFilterItems}
            value={statusFilter}
            onValueChange={selectStatus}
          />
          <FilterSelect
            ariaLabel={t("history.groupFilter.aria")}
            icon={<IconFilter aria-hidden />}
            items={groupFilterItems}
            value={groupFilter}
            onValueChange={selectGroup}
          />
        </>
      }
      footer={
        <DataTablePagination
          page={currentPage}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          summary={t("history.pagination", {
            from: (currentPage - 1) * pageSize + (visibleRecords.length ? 1 : 0),
            to: (currentPage - 1) * pageSize + visibleRecords.length,
            total: filteredRecords.length,
          })}
          onPageChange={setPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
        />
      }
    >
      <DataTable
        density="compact"
        stickyHeader
        className={cn(
          "table-fixed",
          (isLoading || isError || visibleRecords.length === 0) && "h-full",
        )}
      >
        <colgroup>
          <col className="w-[22%]" />
          <col className="w-[13%]" />
          <col className="w-[15%]" />
          <col className="w-[9%]" />
          <col className="w-[10%]" />
          <col className="w-[12%]" />
          <col className="w-[10%]" />
          <col className="w-[15%]" />
        </colgroup>
        <TableHeader>
          <TableRow>
            <TableHead>{t("history.columns.runRecord")}</TableHead>
            <TableHead>{t("history.columns.group")}</TableHead>
            <TableHead>{t("history.columns.result")}</TableHead>
            <TableHead>{t("history.columns.changes")}</TableHead>
            <TableHead>
              <span className="flex items-center gap-1">
                {t("history.columns.highestRisk")}
                <RiskLegendTip aggregate />
              </span>
            </TableHead>
            <TableHead>{t("history.columns.outcome")}</TableHead>
            <TableHead>{t("history.columns.started")}</TableHead>
            <TableHead className="text-right">{t("history.columns.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? <LoadingRows /> : null}
          {isError ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={8} className="p-0">
                <ErrorState message={t("history.loadError")} onRetry={onRetry} />
              </TableCell>
            </TableRow>
          ) : null}
          {!isLoading && !isError && visibleRecords.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={8} className="p-0">
                <Empty className="h-full py-12">
                  <EmptyHeader>
                    <EmptyMedia>
                      <IconSparkles className="size-8 text-muted-foreground/45" aria-hidden />
                    </EmptyMedia>
                    <EmptyTitle>
                      {runs.length === 0
                        ? t("history.empty.noRunsTitle")
                        : t("history.empty.noMatchTitle")}
                    </EmptyTitle>
                    <EmptyDescription>
                      {runs.length === 0
                        ? t("history.empty.noRunsDescription")
                        : t("history.empty.noMatchDescription")}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </TableCell>
            </TableRow>
          ) : null}
          {!isLoading && !isError
            ? visibleRecords.flatMap((record) => {
                if (record.kind === "single") {
                  return [
                    <CampaignRunRow
                      key={record.id}
                      run={record.run}
                      onOpen={() => onOpenRun(record.run.id)}
                    />,
                  ];
                }
                const collapsed = collapsedBatchIds.has(record.id);
                return [
                  <BatchRunRow
                    key={record.id}
                    record={record}
                    collapsed={collapsed}
                    onToggle={() => toggleBatch(record.id)}
                  />,
                  ...(!collapsed
                    ? record.runs.map((run) => (
                        <CampaignRunRow
                          key={run.id}
                          run={run}
                          nested
                          onOpen={() => onOpenRun(run.id)}
                        />
                      ))
                    : []),
                ];
              })
            : null}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}

function BatchRunRow({
  record,
  collapsed,
  onToggle,
}: {
  record: BatchRunRecord;
  collapsed: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const manual = record.triggerType === "manual";
  const startedAt = earliestTimestamp(record.runs.map((run) => run.startedAt));
  const finishedAt = latestTimestamp(record.runs.map((run) => run.finishedAt));
  const allFinished = record.runs.every((run) => run.finishedAt !== null);
  const duration = allFinished && finishedAt ? formatDuration(startedAt, finishedAt) : null;
  const pendingDecisions = record.runs.reduce((total, run) => total + run.pendingDecisions, 0);
  const statusChips = batchStatusChips(record.runs);
  const outcome = batchOutcome(record.runs, pendingDecisions, t);
  return (
    <TableRow className="bg-table-footer hover:bg-subtle/70 [&>td]:h-[52px] [&>td]:py-0">
      {/* 合并前 5 列(运行记录/托管组/结果/变更/最高风险),让状态标签正好落在
          「处理状态」列,与子行对齐。 */}
      <TableCell colSpan={5} className="px-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <DataTableDisclosure
            expanded={!collapsed}
            label={collapsed ? t("history.batch.expand") : t("history.batch.collapse")}
            onToggle={onToggle}
          />
          <button
            type="button"
            className="min-w-[228px] max-w-[280px] text-left"
            onClick={onToggle}
          >
            <span className="block truncate text-[11px] font-semibold leading-3.5 text-ink">
              {t(manual ? "history.batch.manualShopRun" : "history.batch.scheduledShopRun", {
                date: formatBeijingDate(startedAt, locale),
              })}
            </span>
            <span className="mt-0.5 block truncate text-[9px] leading-3 text-muted-foreground">
              {t(manual ? "history.batch.manualSubtitle" : "history.batch.scheduledSubtitle", {
                name: record.runs[0]?.triggeredByName ?? t("history.batch.workspaceMember"),
                time: formatBeijingTime(startedAt),
                duration: duration ? ` · ${duration}` : "",
              })}
            </span>
          </button>
          <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
            <BatchChip label={t("history.batch.campaigns", { count: record.runs.length })} />
            {statusChips.map((chip) => (
              <BatchChip
                key={chip.key}
                tone={chip.tone}
                label={t(`history.batch.${chip.key}`, { count: chip.count })}
              />
            ))}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <OutcomeLabel label={outcome.label} className={outcome.className} />
      </TableCell>
      <TableCell />
      {/* 批次不提供聚合审核入口——一个方案对应一个广告活动,在展开的子行里
          逐个进入;数量语义由上方「N 个待审批」chip 承担(ADP-127 反馈)。 */}
      <TableCell />
    </TableRow>
  );
}

function BatchChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "neutral" | "success" | "warning" | "danger";
}): React.ReactElement {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] shrink-0 items-center rounded-full border px-2 text-[9px] font-medium leading-3",
        tone === "neutral" && "border-line bg-surface text-muted-foreground",
        tone === "success" && "border-green/20 bg-green/5 text-green-11",
        tone === "warning" && "border-amber/25 bg-amber/5 text-amber-11",
        tone === "danger" && "border-red/20 bg-red/5 text-red-11",
      )}
    >
      {label}
    </span>
  );
}

function CampaignRunRow({
  run,
  nested = false,
  onOpen,
}: {
  run: AiOperationsRun;
  nested?: boolean;
  onOpen: () => void;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency } = useFormatters();
  const pendingDecisions = run.pendingDecisions;
  const risk = run.highestRisk;
  const manual = run.triggerType === "manual";
  const detailAvailable = run.detailAvailability === "available";
  return (
    <TableRow className="hover:bg-surface [&>td]:h-[60px] [&>td]:py-0">
      <TableCell>
        <div className={cn("flex min-w-0 items-center", nested ? "gap-3.5" : "gap-2")}>
          {nested ? (
            <BatchChildConnector />
          ) : (
            <span className="flex size-7 shrink-0 items-center justify-center rounded-[7px] border border-line bg-subtle text-muted-foreground">
              {manual ? <IconUser className="size-3.5" /> : <IconRobot className="size-3.5" />}
            </span>
          )}
          <span className="min-w-0">
            {run.campaignId ? (
              <Link
                to="/campaigns/$id"
                params={{ id: run.campaignId }}
                className="block truncate text-[11px] font-semibold text-ink underline-offset-4 hover:underline"
              >
                {run.campaignName ?? t("history.row.removedCampaign")}
              </Link>
            ) : (
              <span className="block truncate text-[11px] font-semibold text-ink">
                {t("history.row.shopWide")}
              </span>
            )}
            <span className="mt-0.5 flex items-center gap-1.5 truncate text-[9px] text-muted-foreground">
              {nested ? null : <TriggerBadge manual={manual} />}
              {/* ADP-110: disambiguate runs across shops in the aggregated history */}
              {run.shopName ? <span className="truncate">{run.shopName} ·</span> : null}
              {manual && run.triggeredByName
                ? t("history.row.byName", { name: run.triggeredByName })
                : ""}
              #{run.id.slice(0, 8).toUpperCase()}
            </span>
          </span>
        </div>
      </TableCell>
      <TableCell>
        {run.managedGroupId ? (
          <Link
            to="/managed-groups/$id"
            params={{ id: run.managedGroupId }}
            className="block truncate text-[10px] text-ink underline-offset-4 hover:underline"
          >
            {run.managedGroupName ?? run.managedGroupId.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-[10px] text-muted-foreground">
            {t("history.groupFilter.ungrouped")}
          </span>
        )}
      </TableCell>
      <TableCell>
        <span className="block truncate text-[10px] font-medium text-ink">
          {resultTitle(run, pendingDecisions, t)}
        </span>
        <span className="mt-0.5 block truncate text-[9px] text-muted-foreground">
          {runResultDetail(run, pendingDecisions, t)}
        </span>
      </TableCell>
      <TableCell className="text-[10px] text-ink">
        {/* ADP-127: 单竞价动作直接展示价格变化,比"1 项变更"更直观 */}
        {run.singleBidChange ? (
          <span className="font-mono tabular-nums">
            {formatCurrency(run.singleBidChange.oldBid)} →{" "}
            {formatCurrency(run.singleBidChange.newBid)}
          </span>
        ) : run.candidateActions > 0 ? (
          run.jobType === "daily_search_term_mining" ? (
            t("history.row.suggestions", { value: run.candidateActions })
          ) : (
            t("history.row.changes", { value: run.candidateActions })
          )
        ) : (
          t("history.row.noChanges")
        )}
      </TableCell>
      <TableCell>
        {risk ? <RiskBadge risk={risk} /> : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell>
        <OutcomeIndicator run={run} hasDecision={pendingDecisions > 0} />
      </TableCell>
      <TableCell className="text-[9px] text-muted-foreground">
        {formatBeijingTime(run.startedAt)}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <ControlButton
            controlSize="compact"
            variant="outline"
            disabled={!detailAvailable}
            onClick={onOpen}
          >
            {!detailAvailable
              ? t("history.row.legacyUnavailable")
              : isReviewableProposal(run)
                ? t("history.row.reviewPlan")
                : t("history.row.viewRecord")}
          </ControlButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function BatchChildConnector(): React.ReactElement {
  return (
    <span
      data-slot="batch-child-connector"
      aria-hidden
      className="flex w-[30px] shrink-0 items-center justify-center"
    >
      <span className="h-[18px] w-3 shrink-0 rounded-bl-[5px] border-b border-l border-line" />
    </span>
  );
}

function TriggerBadge({ manual }: { manual: boolean }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <Badge variant="neutral" size="compact" className="rounded-sm font-medium">
      {manual ? t("history.triggerBadge.manual") : t("history.triggerBadge.scheduled")}
    </Badge>
  );
}

function RiskBadge({ risk }: { risk: RiskLevel }): React.ReactElement {
  return <ApprovalStatusBadge value={risk} />;
}

function OutcomeIndicator({
  run,
  hasDecision,
}: {
  run: AiOperationsRun;
  hasDecision: boolean;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const outcome = runOutcome(run, hasDecision, t);
  return <OutcomeLabel label={outcome.label} className={outcome.className} />;
}

function OutcomeLabel({
  label,
  className,
}: {
  label: string;
  className: string;
}): React.ReactElement {
  return (
    <span className={cn("flex items-center gap-[5px] text-[10px] font-semibold", className)}>
      <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
}

function LoadingRows(): React.ReactElement {
  return (
    <>
      {["loading-1", "loading-2", "loading-3", "loading-4", "loading-5"].map((key) => (
        <TableRow key={key} className="hover:bg-transparent [&>td]:h-[52px] [&>td]:py-0">
          <TableCell colSpan={8}>
            <Skeleton className="h-5 w-full rounded-md" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export function buildRunRecords(runs: AiOperationsRun[]): RunRecord[] {
  const records: RunRecord[] = [];
  const byBatchId = new Map<string, BatchRunRecord>();
  for (const run of runs) {
    if (run.batchId) {
      const existing = byBatchId.get(run.batchId);
      if (existing) existing.runs.push(run);
      else {
        const record: BatchRunRecord = {
          kind: "batch",
          id: run.batchId,
          triggerType: run.triggerType,
          runs: [run],
        };
        byBatchId.set(run.batchId, record);
        records.push(record);
      }
    } else {
      records.push({ kind: "single", id: run.id, run });
    }
  }
  return records;
}

export function filterRunsByManagedGroup(
  runs: AiOperationsRun[],
  managedGroupId: string,
): AiOperationsRun[] {
  if (managedGroupId === "all") return runs;
  return runs.filter((run) =>
    managedGroupId === "ungrouped"
      ? run.managedGroupId === null
      : run.managedGroupId === managedGroupId,
  );
}

function recordRuns(record: RunRecord): AiOperationsRun[] {
  return record.kind === "batch" ? record.runs : [record.run];
}

function matchesStatus(run: AiOperationsRun, filter: RunStatusFilter): boolean {
  if (filter === "all") return true;
  // 与"处理状态"列同源推导,筛选项 = 列上可见的状态。
  const status = runDisplayStatus(run, run.pendingDecisions > 0);
  if (filter === "needs-decision") return status.outcome === "needsDecision";
  if (filter === "approved")
    return status.outcome === "approved" || status.outcome === "approvedPendingExecution";
  if (filter === "executed") return status.outcome === "executed";
  if (filter === "rolled-back") return status.outcome === "rolledBack";
  if (filter === "rejected") return status.outcome === "rejected";
  if (filter === "expired") return status.outcome === "expired";
  if (filter === "recommended")
    return status.outcome === "recommended" || status.outcome === "partialRecommended";
  if (filter === "partial") return status.outcome === "partial";
  if (filter === "analyzing") return status.outcome === "analyzing";
  if (filter === "failed") return status.incident !== null;
  // 已完成 = 剩余的终态兜底(完成/部分完成/已观察)。
  return (
    status.outcome === "completed" ||
    status.outcome === "noChanges" ||
    status.outcome === "observed"
  );
}

/**
 * 运行级筛选:批次记录只保留匹配的子运行,行首统计与展开列表随之收窄——
 * 记录级 some() 命中会把整个批次(cron 一批上百个运行)原样带出来。
 */
export function filterRunRecords(records: RunRecord[], filter: RunStatusFilter): RunRecord[] {
  if (filter === "all") return records;
  const filtered: RunRecord[] = [];
  for (const record of records) {
    if (record.kind === "single") {
      if (matchesStatus(record.run, filter)) filtered.push(record);
      continue;
    }
    const runs = record.runs.filter((run) => matchesStatus(run, filter));
    if (runs.length > 0) filtered.push({ ...record, runs });
  }
  return filtered;
}

// 「审核」只给真正有待审批决策的运行;已建议/已审完的方案是只读记录,
// 统一「查看记录」(ADP-127 审批语义统一)。
function isReviewableProposal(run: AiOperationsRun): boolean {
  return run.jobType === "daily_bid_optimization" && run.pendingDecisions > 0;
}

function isRunStatusFilter(value: string): value is RunStatusFilter {
  return STATUS_FILTER_VALUES.some((item) => item === value);
}

export function batchOutcome(
  runs: AiOperationsRun[],
  pendingDecisions: number,
  t: TranslateFn,
): { label: string; className: string } {
  const statuses = runs.map((run) => runDisplayStatus(run, run.pendingDecisions > 0));
  if (pendingDecisions > 0)
    return { label: t("history.outcome.needsDecision"), className: "text-amber-11" };
  if (
    statuses.some(({ outcome }) =>
      ["analyzing", "finalizing", "approvedPendingExecution", "executing"].includes(outcome ?? ""),
    )
  )
    return { label: t("history.outcome.analyzing"), className: "text-amber-11" };
  if (statuses.every(({ outcome }) => outcome === null))
    return { label: t("history.outcome.failed"), className: "text-red-11" };
  if (
    statuses.some(
      ({ outcome }) =>
        outcome === null || outcome === "partial" || outcome === "partialRecommended",
    )
  )
    return { label: t("history.outcome.partial"), className: "text-amber-11" };
  const incidentCount = statuses.filter(({ incident }) => incident !== null).length;
  if (incidentCount > 0) {
    return {
      label: t("history.outcome.batchCompletedWithIncidents", { count: incidentCount }),
      className: "text-red-11",
    };
  }
  return { label: t("history.outcome.batchCompleted"), className: "text-green-11" };
}

type BatchStatusChipKey =
  | "noChanges"
  | "executed"
  | "approved"
  | "rejected"
  | "expired"
  | "rolledBack"
  | "recommended"
  | "partial"
  | "needDecision"
  | "analyzing"
  | "executionFailed"
  | "finalizationFailed"
  | "rollbackFailed"
  | "unknownFailure";

interface BatchStatusChipSummary {
  count: number;
  key: BatchStatusChipKey;
  tone: "neutral" | "success" | "warning" | "danger";
}

const BATCH_STATUS_CHIP_ORDER: ReadonlyArray<{
  key: BatchStatusChipKey;
  tone: BatchStatusChipSummary["tone"];
}> = [
  { key: "noChanges", tone: "success" },
  { key: "executed", tone: "success" },
  { key: "approved", tone: "success" },
  { key: "rejected", tone: "neutral" },
  { key: "expired", tone: "warning" },
  { key: "rolledBack", tone: "neutral" },
  { key: "recommended", tone: "neutral" },
  { key: "partial", tone: "warning" },
  { key: "needDecision", tone: "warning" },
  { key: "analyzing", tone: "neutral" },
  { key: "executionFailed", tone: "danger" },
  { key: "finalizationFailed", tone: "danger" },
  { key: "rollbackFailed", tone: "danger" },
  { key: "unknownFailure", tone: "danger" },
];

/** Keep business outcomes and technical incidents visible as separate batch counts. */
export function batchStatusChips(runs: AiOperationsRun[]): BatchStatusChipSummary[] {
  const counts = new Map<BatchStatusChipKey, number>();
  const increment = (key: BatchStatusChipKey): void => {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  };

  for (const run of runs) {
    const { outcome, incident } = runDisplayStatus(run, run.pendingDecisions > 0);
    if (outcome === "noChanges" || outcome === "completed" || outcome === "observed") {
      increment("noChanges");
    } else if (outcome === "executed") increment("executed");
    else if (outcome === "approved" || outcome === "approvedPendingExecution") {
      increment("approved");
    } else if (outcome === "rejected") increment("rejected");
    else if (outcome === "expired") increment("expired");
    else if (outcome === "rolledBack") increment("rolledBack");
    else if (outcome === "recommended" || outcome === "partialRecommended") {
      increment("recommended");
    } else if (outcome === "partial") increment("partial");
    else if (outcome === "needsDecision") increment("needDecision");
    else if (outcome === "analyzing" || outcome === "finalizing" || outcome === "executing") {
      increment("analyzing");
    }

    if (incident) increment(incident);
  }

  return BATCH_STATUS_CHIP_ORDER.flatMap(({ key, tone }) => {
    const count = counts.get(key) ?? 0;
    return count > 0 ? [{ count, key, tone }] : [];
  });
}

function resultTitle(run: AiOperationsRun, pendingDecisions: number, t: TranslateFn): string {
  if (pendingDecisions > 0) return t("history.resultTitle.completePlan");
  if (run.jobType === "daily_search_term_mining" && run.candidateActions > 0)
    return t("history.resultTitle.searchTermRecs");
  if (run.candidateActions > 0) return t("history.resultTitle.efficiency");
  return t("history.resultTitle.learningPhase");
}

function latestTimestamp(values: Array<string | null>): string | null {
  return values.reduce<string | null>((latest, value) => {
    if (!value) return latest;
    return !latest || new Date(value) > new Date(latest) ? value : latest;
  }, null);
}

function earliestTimestamp(values: string[]): string {
  return values.reduce((earliest, value) =>
    new Date(value) < new Date(earliest) ? value : earliest,
  );
}

function formatDuration(startedAt: string, finishedAt: string): string {
  const totalSeconds = Math.max(
    0,
    Math.round((new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000),
  );
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}
